import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type { SPANavSwitcher, SPANavSwitcherOption } from '../SPANav';

export interface ProgramSummary {
  archived: boolean;
  color: string | null;
  custom: boolean;
  science: boolean;
  title: string;
}

interface RawProgram {
  archived: boolean;
  color: string | null;
  custom: boolean;
  full_title_with_edition: string;
  id: number;
  science: boolean;
}

// Every program, keyed by id: the one fetch behind the lessons page title, the rails' program tint
// and switcher, and the Shared Programs cells, which need titles for arbitrary other programs.
export const useProgramSummaries = () => useQuery({
  queryFn: async () => {
    const { data } = await axios.get(Routes.plato_api_programs_path({
      only: ['id', 'archived', 'color', 'custom', 'full_title_with_edition', 'science'],
    }));
    const summaries: Record<number, ProgramSummary> = {};
    ((data.data || []) as RawProgram[]).forEach((program) => {
      summaries[program.id] = {
        archived: program.archived,
        color: program.color,
        custom: program.custom,
        science: program.science,
        title: program.full_title_with_edition,
      };
    });
    return summaries;
  },
  queryKey: [QueryKeys.ContentPrograms, 'summaries'],
});

export const useProgramSummary = (programId: number) => {
  const { data: summaries, isLoading } = useProgramSummaries();
  const summary = summaries?.[programId];
  return {
    color: summary?.color ?? null,
    isLoading,
    science: summary?.science ?? false,
    title: summaries ? (summary?.title || `Program ${programId}`) : '',
  };
};

// A stable empty list while loading: a fresh array each render would cascade through the
// memoized switcher into a SPANavSlot re-register loop.
const NO_OPTIONS: SPANavSwitcherOption[] = [];

// Standard, unarchived programs as switcher options — the same curated set the Programs table
// shows. Untitled program stubs carry the literal "{placeholder}" template text, as excluded in
// ReferenceMaterials/useFilterOptions.
export const useProgramOptions = () => {
  const { data: summaries, isLoading } = useProgramSummaries();
  const options = useMemo(() => {
    if (!summaries) return NO_OPTIONS;
    return Object.entries(summaries)
      .filter(([, program]) => !program.custom && !program.archived
        && program.title && !program.title.includes('{placeholder}'))
      .map(([id, program]) => ({ label: program.title, value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [summaries]);

  return { isLoading, options };
};

// The program switcher a drill rail shows in its header. `onSelect` must be referentially stable
// for the result to be, which SPANavSlot needs.
export const useProgramSwitcher = (programId: number, onSelect: (value: string) => void): SPANavSwitcher => {
  const { isLoading, options } = useProgramOptions();

  return useMemo(() => ({
    loading: isLoading,
    onSelect,
    options,
    placeholder: 'Switch program...',
    value: String(programId),
  }), [isLoading, onSelect, options, programId]);
};
